/**
 * BT-055 — Gabarit liste officielle Gilles (structure + validation).
 * Aucun import massif — préparation collecte uniquement.
 */

export const OFFICIAL_LIST_BT_ID = "BT-GSD-KARATE-GABARIT-LISTE-OFFICIELLE-GILLES-055";

export const OFFICIAL_LIST_TEMPLATE_FILENAME = "karate-liste-officielle-gilles-055.csv";

/** Chemin public téléchargeable depuis l'UI Admin. */
export const OFFICIAL_LIST_TEMPLATE_PUBLIC_PATH = `/templates/${OFFICIAL_LIST_TEMPLATE_FILENAME}`;

/** Chemin canonique pilotage (référence documentation). */
export const OFFICIAL_LIST_TEMPLATE_PILOTAGE_PATH =
  "00_PILOTAGE/templates/karate-liste-officielle-gilles-055.csv";

export const OFFICIAL_LIST_SCHEMA_PILOTAGE_PATH =
  "00_PILOTAGE/templates/karate-liste-officielle-gilles-055.schema.md";

export const OFFICIAL_LIST_REQUIRED_COLUMNS = [
  "action",
  "personne_type",
  "first_name",
  "last_name",
  "display_name",
  "official_email",
  "phone",
  "role",
  "dojo_name",
  "dojo_city",
  "account_needed",
  "temporary_access_needed",
  "must_change_password",
  "status",
  "notes",
  "source_confirmation",
];

export const OFFICIAL_LIST_ALLOWED_VALUES = {
  action: ["create", "update", "deactivate", "confirm_only"],
  personne_type: ["hanshi", "sensei", "sempai", "student", "parent", "responsible", "other"],
  role: ["hanshi", "admin", "sensei", "sempai", "student", "parent", "responsible", "none"],
  account_needed: ["yes", "no", "to_confirm"],
  temporary_access_needed: ["yes", "no", "to_confirm"],
  must_change_password: ["yes", "no", "default_yes"],
  status: ["active", "to_confirm", "inactive"],
  source_confirmation: ["confirmed_by_gilles", "to_confirm", "not_confirmed"],
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * @param {string[]} headers
 * @returns {{ ok: boolean, missing: string[] }}
 */
export function validateOfficialListHeaders(headers) {
  const normalized = headers.map((h) => h.trim().toLowerCase());
  const missing = OFFICIAL_LIST_REQUIRED_COLUMNS.filter((col) => !normalized.includes(col));
  return { ok: missing.length === 0, missing };
}

/**
 * @param {Record<string, string>} row
 * @returns {string[]}
 */
export function validateOfficialListRow(row) {
  const errors = [];
  const get = (k) => (row[k] ?? "").trim();

  for (const [field, allowed] of Object.entries(OFFICIAL_LIST_ALLOWED_VALUES)) {
    const val = get(field);
    if (val && !allowed.includes(val)) {
      errors.push(`${field}: valeur non autorisée « ${val} »`);
    }
  }

  const email = get("official_email");
  if (email) {
    if (!EMAIL_RE.test(email)) errors.push("official_email: format invalide");
    if (/\.local$/i.test(email.split("@")[1] || "")) {
      errors.push("official_email: domaine .local interdit comme courriel officiel");
    }
  }

  const role = get("role");
  const dojo = get("dojo_name");
  if ((role === "sensei" || role === "sempai") && !dojo) {
    const notes = get("notes");
    if (!/à confirmer|to confirm/i.test(notes)) {
      errors.push("dojo_name: requis pour sensei/sempai ou note « à confirmer »");
    }
  }

  if (role === "parent") {
    const notes = get("notes");
    if (!notes) errors.push("notes: enfant lié ou « à confirmer » requis pour parent");
  }

  if (get("account_needed") === "yes" && !email) {
    const src = get("source_confirmation");
    if (src !== "to_confirm" && !/à confirmer|to confirm/i.test(get("notes"))) {
      errors.push("official_email: requis si account_needed=yes (ou marquer à confirmer)");
    }
  }

  return errors;
}

/**
 * @param {string} csvText
 * @returns {{ ok: boolean, headerOnly: boolean, rowCount: number, errors: string[] }}
 */
export function parseAndValidateOfficialListCsv(csvText) {
  const lines = csvText
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length === 0) {
    return { ok: false, headerOnly: false, rowCount: 0, errors: ["CSV vide"] };
  }
  const headers = lines[0].split(",").map((h) => h.trim());
  const headerCheck = validateOfficialListHeaders(headers);
  const errors = [];
  if (!headerCheck.ok) {
    errors.push(`Colonnes manquantes: ${headerCheck.missing.join(", ")}`);
  }
  const dataLines = lines.slice(1);
  const rowCount = dataLines.length;
  return {
    ok: errors.length === 0,
    headerOnly: rowCount === 0,
    rowCount,
    errors,
  };
}
